import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { http, STORE_LABELS } from "../lib/api";
import { BookCover, Stamp, MadeWith } from "../components/Bits";
import { PageLoading } from "../context/AuthContext";
import JoinReaders from "../components/JoinReaders";

export const NotFound = ({ what = "page" }) => (
  <div className="max-w-xl mx-auto py-32 px-6 text-center" data-testid="not-found">
    <p className="label-mono mb-3">404 / Out of print</p>
    <h1 className="font-display text-4xl">This {what} isn't on the shelf.</h1>
    <Link to="/discover" className="btn-secondary mt-8" data-testid="not-found-discover-link">Browse the shelf</Link>
  </div>
);

function BuyButtons({ book }) {
  const links = Object.entries(book.buy_links || {});
  if (!links.length) return <p className="label-mono" data-testid="book-no-buy-links">Store links coming soon</p>;
  const click = (store) => {
    if (book.published) http.post(`/public/books/${book.slug}/click`, { store }).catch(() => {});
  };
  return (
    <div className="flex flex-wrap gap-3" data-testid="book-buy-buttons">
      {links.map(([store, url], i) => (
        <a key={store} href={url} target="_blank" rel="noopener noreferrer" onClick={() => click(store)}
          className={i === 0 ? "btn-primary" : "btn-secondary"} data-testid={`book-buy-${store}-button`}>
          {STORE_LABELS[store]} <ArrowUpRight size={13} />
        </a>
      ))}
    </div>
  );
}

function Hero({ book }) {
  const tags = [...new Set([...(book.kit.tribe_tags || []), ...book.moods, ...book.themes])];
  return (
    <section className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
      <div className="lg:col-span-5 lg:sticky lg:top-24">
        <BookCover src={book.cover_url} title={book.title} author={book.author?.name} className="w-[78%] sm:w-[60%] lg:w-full mx-auto -rotate-1" testid="book-cover" />
      </div>
      <div className="lg:col-span-7 rise">
        {!book.published && <p className="stamp stamp-active mb-6" data-testid="book-draft-preview-banner">Draft preview — only you can see this</p>}
        {book.is_sample && <p className="stamp mb-6" data-testid="book-sample-banner">Sample book page</p>}
        <p className="label-mono">{[book.genre, ...book.formats].filter(Boolean).join(" · ")}</p>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight leading-[1.02] mt-4" data-testid="book-title">{book.title}</h1>
        {book.subtitle && <p className="font-display italic text-2xl text-[#5C584E] mt-3">{book.subtitle}</p>}
        <Link to={`/a/${book.author?.slug}`} className="inline-block mt-5 font-type text-xs uppercase tracking-widest border-b border-[#121210] hover:text-[#C84B31] hover:border-[#C84B31]" data-testid="book-author-link">
          by {book.author?.name}
        </Link>
        {book.kit.hook && <p className="font-display text-2xl sm:text-3xl leading-snug mt-10 border-l-2 border-[#C84B31] pl-6" data-testid="book-hook">{book.kit.hook}</p>}
        <div className="flex flex-wrap gap-2.5 mt-8">{tags.map((t) => <Stamp key={t}>{t}</Stamp>)}</div>
        <div className="mt-10 flex flex-wrap items-center gap-6">
          <BuyButtons book={book} />
          {book.price && <span className="font-display text-2xl" data-testid="book-price">{book.price}</span>}
        </div>
        <Body book={book} />
      </div>
    </section>
  );
}

function Body({ book }) {
  const fans = book.kit.for_fans_of?.length ? book.kit.for_fans_of : book.comps;
  const blurb = book.kit.blurb_back || book.synopsis;
  const bio = book.kit.bio_medium || book.author?.bio;
  return (
    <div className="mt-16 space-y-14">
      {blurb && <div className="text-lg leading-[1.8] whitespace-pre-line first-letter:font-display first-letter:text-6xl first-letter:float-left first-letter:mr-2 first-letter:leading-[0.85]" data-testid="book-blurb">{blurb}</div>}
      {fans?.length > 0 && (
        <div data-testid="book-for-fans-of">
          <p className="label-mono mb-4">For fans of</p>
          <div className="flex flex-wrap gap-x-8 gap-y-2">{fans.map((f) => <span key={f} className="font-display italic text-2xl">{f}</span>)}</div>
        </div>
      )}
      {bio && (
        <div className="border-t border-[#E5DEC9] pt-8" data-testid="book-author-bio">
          <p className="label-mono mb-3">About the author</p>
          <p className="leading-relaxed text-[#5C584E]">{bio}</p>
        </div>
      )}
      {book.published && !book.is_sample && <JoinReaders endpoint={`/public/books/${book.slug}/subscribe`} authorName={book.author?.name} testid="book-join" />}
      <MadeWith />
    </div>
  );
}

export default function PublicBook() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [book, setBook] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    const prev = document.title;
    return () => {
      document.title = prev;
    };
  }, []);

  useEffect(() => {
    const key = `viewed:${slug}`;
    const seen = sessionStorage.getItem(key);
    sessionStorage.setItem(key, "1");
    http.get(`/public/books/${slug}`, { params: { ref: seen ? "seen" : params.get("ref") || "" } })
      .then(({ data }) => { setBook(data); document.title = `${data.title} — ${data.author?.name}`; })
      .catch(() => setMissing(true));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (missing) return <NotFound what="book" />;
  if (!book) return <PageLoading />;
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-10" data-testid="public-book-page">
      <Hero book={book} />
    </div>
  );
}
